<script setup lang="ts">
import { computed } from 'vue'

import Button from '@satch-ui/components/button/Button.vue'
import Status from '@satch-ui/components/status/Status.vue'
import { formatDate } from '@shared/fondof-fn/date/format'
import type { ProductCardContentProps } from '@storefront-satch/components/molecules/product/card/types'
import { t } from '@storefront-satch/lib/translations'

import { ArrowRightIcon, PlusIcon } from '@fond-of/satch-icons'

const props = withDefaults(defineProps<ProductCardContentProps>(), {
  price: undefined,
  originalPrice: undefined,
  currencyCode: undefined,
  isOffer: undefined,
  showAtcButton: false,
  atcButtonIsDisabled: false,
  atcButtonIsFullWidth: false,
  variant: 'black',
  buttonVariant: 'outlineBrandColor',
})

const emit = defineEmits<{
  buttonClick: []
}>()

const showAddToCardButton = computed(() => {
  if (
    props.isComingSoonLaunch ||
    props.isComingSoonRestock ||
    props.isComingSoon
  ) {
    return false
  }

  return props.product.available && props.showAtcButton
})

const productEdition = computed(() => {
  if (!props.product.attributes.edition) {
    return ''
  }

  const [productEdition] = props.product.attributes.edition

  return productEdition
})

const isSoldOut = computed(() => {
  return !(
    !props.product.available &&
    props.isComingSoon &&
    props.isComingSoonRestock
  )
})
</script>

<template>
  <div
    class="flex grow flex-col justify-between"
    :class="{
      'sm:absolute sm:left-3/4 sm:w-[70%]': props.isHorizontal,
      'mt-4': !props.isHorizontal,
    }"
  >
    <div
      :class="{
        'text-primary': props.variant === 'brand color',
        'text-black': props.variant === 'black',
        'text-white': props.variant === 'white',
      }"
    >
      <div
        v-if="props.showEdition && productEdition && props.isHorizontal"
        class="font-sans-condensed text-2xs mb-[5px] uppercase tracking-wider"
      >
        {{ productEdition }}
      </div>
      <h3 class="font-sans text-xs">
        {{ props.product.attributes.model }}
        <span class="font-sans-bold block text-base">
          {{ props.product.attributes.style }}
        </span>
      </h3>
      <div class="mb-2 flex space-x-2.5 font-sans text-xs">
        <div
          class="inline-block"
          :class="{ 'line-through opacity-70': props.isOffer }"
        >
          <template v-if="props.isOffer">
            {{ props.originalPrice }}
          </template>
          <template v-else>
            {{ props.price }}
          </template>
        </div>

        <div
          v-if="props.isOffer"
          class="text-red-light font-sans-bold inline-block"
        >
          {{ props.price }}
        </div>
      </div>

      <template v-if="!props.product.available">
        <Status
          v-if="props.isComingSoon"
          :class="{
            'mb-2': showAddToCardButton,
          }"
          variant="comingSoon"
        >
          {{ t('product.comingSoon') }}
        </Status>
        <Status
          v-else-if="props.isComingSoonRestock"
          :class="{
            'mb-2': showAddToCardButton,
          }"
          variant="gray"
        >
          {{ t('product.availableAgainAt') }}
          {{ props.restockDate && formatDate(props.restockDate) }}
        </Status>
        <Status
          v-else
          :class="{
            'mb-2': showAddToCardButton,
          }"
          variant="soldOut"
        >
          {{ t('product.soldOut') }}
        </Status>
      </template>
      <template v-else>
        <Status
          v-if="props.isComingSoonLaunch"
          :class="{
            'mb-2': showAddToCardButton,
          }"
          variant="secondary"
        >
          {{ t('product.availableAt') }}
          {{ props.launchDate && formatDate(props.launchDate) }}
        </Status>
      </template>
    </div>

    <div>
      <Button
        v-if="showAddToCardButton"
        :variant="props.buttonVariant"
        :class="['mb-4 block', { 'w-full': atcButtonIsFullWidth }]"
        :disabled="atcButtonIsDisabled"
        @click.stop.prevent="() => emit('buttonClick')"
      >
        <slot>
          <span>
            {{ t('product.addToCart') }}
          </span>
          <PlusIcon class="text-primary size-3.5" />
        </slot>
      </Button>
    </div>

    <div
      v-if="props.showProductLink && !isSoldOut"
      class="mt-2 lg:mt-3"
    >
      <Button :variant="props.buttonVariant">
        {{ t('toProduct') }}

        <ArrowRightIcon class="size-4" />
      </Button>
    </div>
  </div>
</template>
